import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListSubheader from "@mui/material/ListSubheader";
import Popper from "@mui/material/Popper";
import { useTheme, styled } from "@mui/material/styles";
import { VariableSizeList } from "react-window";
import Typography from "@mui/material/Typography";
import { CircularProgress } from "@mui/material";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;

  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
  };

  //   if (dataSet.hasOwnProperty("group")) {
  //     return (
  //       <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
  //         {dataSet.group}
  //       </ListSubheader>
  //     );
  //   }

  return (
    <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
      {`${dataSet[1]?.text}`}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref
) {
  const { children, ...other } = props;
  const itemData = [];
  children.forEach((item) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => {
    if (child.hasOwnProperty("group")) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

function random(length) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return result;
}

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});

const OPTIONS = Array.from(new Array(10000))
  .map(() => random(10 + Math.ceil(Math.random() * 20)))
  .sort((a, b) => a.toUpperCase().localeCompare(b.toUpperCase()));

//   console.log(OPTIONS);
export default function AutoCompleteVariableSize({
  options,
  defaultOption,
  selectedOption,
  handleChange,
  isLoading
}) {
  const [_options, setOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState({
    text: defaultOption?.text ?? "Select",
    value: defaultOption?.value ?? 0,
  });

  useEffect(() => {
    setOptions(options);
    // if (selectedOption)
    //   setSelectedItem({
    //     text: selectedOption.text,
    //     value: selectedOption.value,
    //   });
  }, [options]);

  useEffect(() => {
    // console.log(selectedOption);
    //setOptions(options.map((x) => x.text));
    const existingObject = options.find((s) => s.value == selectedOption);
    if (existingObject && selectedItem.value != selectedOption) {
      setSelectedItem({
        text: existingObject.text,
        value: existingObject.value,
      });
    } else {
      setSelectedItem({
        text: defaultOption?.text,
        value: defaultOption?.value,
      });
    }
  }, [selectedOption]);

  const handleChange1 = (event, newValue, reason, details) => {
    // console.log(event, newValue, reason, details);
    handleChange(newValue?.value);
  };
  return (
    <Autocomplete
      id="virtualize-demo"
      sx={{ width: 300 }}
      disableListWrap
      className="mx-2 mb-2"
      value={selectedItem}
      onChange={(event, newValue, reason, details) => {
        handleChange1(event, newValue, reason, details);
      }}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        return option?.text;
      }}
      PopperComponent={StyledPopper}
      ListboxComponent={ListboxComponent}
      options={_options}
      //   groupBy={(option) => option[0].toUpperCase()}
      renderInput={(params) => (
        <TextField
          className="custom-dropdown-text-field"
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress
                    className="me-5"
                    color="inherit"
                    size={20}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(props, option, state) => [props, option, state.index]}
      // TODO: Post React 18 update - validate this conversion, look like a hidden bug
      //   renderGroup={(params) => params}
    />
  );
}
